/**
 * Cookie Consent Banner Component
 *
 * P3-3: ビジネス・法務要件（Cookie同意）
 * - 初回訪問時にCookie同意バナーを表示
 * - 同意状態をlocalStorageに永続化
 * - 再訪問時は同意済みなら非表示
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const CONSENT_KEY = "jarvis-cookie-consent";
const CONSENT_VERSION = "1.0";

interface ConsentState {
  accepted: boolean;
  version: string;
  timestamp: string;
}

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if consent has already been given
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      try {
        const consent: ConsentState = JSON.parse(stored);
        // If consent version matches and was accepted, don't show banner
        if (consent.accepted && consent.version === CONSENT_VERSION) {
          return;
        }
      } catch {
        // Invalid stored data, show banner
      }
    }
    // Small delay to avoid layout shift on initial load
    const timer = setTimeout(() => setShowBanner(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    const consent: ConsentState = {
      accepted: true,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleClose = () => {
    // Close without accepting - banner will show again on next visit
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background border-t shadow-lg animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="container mx-auto max-w-4xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <h2 id="cookie-consent-title" className="text-sm font-semibold mb-1">
            Cookieの使用について
          </h2>
          <p id="cookie-consent-description" className="text-sm text-muted-foreground">
            当サイトでは、サービス改善およびユーザー体験向上のためにCookieを使用しています。
            「同意する」をクリックすることで、
            <a href="/privacy" className="text-primary hover:underline">
              プライバシーポリシー
            </a>
            に基づくCookieの使用に同意したものとみなされます。
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={handleAccept} size="sm">
            同意する
          </Button>
          <Button
            onClick={handleClose}
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="閉じる"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
